/**
 * CampaignWizard — 3-step modal for the "Opprett kampanje" Quick Action.
 *
 * Step 1  Audience  — pick a segment (inactive / all / has-email /
 *                     no-upcoming / has-upcoming) + channel (SMS / email)
 *                     and campaign type ("Vi savner deg", offer, reminder,
 *                     custom).
 * Step 2  Preview   — generate a single template message via the existing
 *                     `/api/ai/generate-followup` endpoint, using the first
 *                     recipient as the example. Body is editable.
 * Step 3  Save      — write one draft per recipient via `/api/followups`,
 *                     personalising the body by substituting `{{navn}}` (or
 *                     the example customer's name) with each recipient's
 *                     name. Drafts can be sent later from the existing
 *                     "Meldinger / Utkast" tab.
 *
 * Reuses everything that already exists: customer segment filtering,
 * AI message generation, draft persistence. No new backend surface.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Mail, MessageSquare, Users, UserMinus,
  CalendarClock, CalendarX, AtSign, ChevronLeft, ChevronRight,
  Megaphone, Heart, BellRing, PenLine, Check, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "@/components/premium";

// ---------- Static option lists ----------
//
// Each audience option declares the customer-list endpoint params it needs
// plus an optional client-side filter so we don't depend on backend changes.
const AUDIENCES = [
  {
    id: "inactive_60", icon: UserMinus,
    label: "Inaktive kunder",
    sub: "Ikke vært innom på 60+ dager",
    params: { segment: "inactive_60" },
  },
  {
    id: "all", icon: Users,
    label: "Alle kunder",
    sub: "Hele kundebasen",
    params: {},
  },
  {
    id: "has_email", icon: AtSign,
    label: "Kunder med e-post",
    sub: "Bare de som kan motta e-post",
    params: {},
    filter: (c) => !!c.email,
  },
  {
    id: "no_upcoming", icon: CalendarX,
    label: "Ingen ny booking",
    sub: "Har vært før, ingen kommende time",
    params: { segment: "no_upcoming" },
  },
  {
    id: "upcoming", icon: CalendarClock,
    label: "Kommende booking",
    sub: "Har en time bestilt",
    params: { segment: "upcoming" },
  },
];

const CAMPAIGN_TYPES = [
  {
    id: "missed_you", icon: Heart,
    label: "Vi savner deg",
    sub: "Vekk en kunde som ikke har vært innom",
    purpose: "inaktiv_kunde",
    defaultHint: "Det er en stund siden sist — vi vil gjerne se deg igjen.",
  },
  {
    id: "offer", icon: Megaphone,
    label: "Tilbud / kampanje",
    sub: "Et spesielt tilbud eller rabatt",
    purpose: "bursdag_kampanje",
    defaultHint: "Beskriv tilbudet kort, f.eks. '15 % rabatt før månedsslutt'.",
  },
  {
    id: "rebook", icon: BellRing,
    label: "Påminnelse om ny booking",
    sub: "Hint om at det er tid for neste time",
    purpose: "paaminnelse_time",
    defaultHint: "Minn kunden vennlig på å booke neste time.",
  },
  {
    id: "custom", icon: PenLine,
    label: "Egendefinert melding",
    sub: "Skriv selv — ingen AI",
    purpose: null,
    defaultHint: "",
  },
];

// ---------- Helpers ----------

function audienceFor(id) { return AUDIENCES.find((a) => a.id === id) || AUDIENCES[0]; }
function typeFor(id) { return CAMPAIGN_TYPES.find((t) => t.id === id) || CAMPAIGN_TYPES[0]; }

/**
 * Personalise a template body for a single recipient. Replaces both the
 * example customer's literal name AND the `{{navn}}` placeholder with the
 * recipient's first name. Falls back to "kunde" if no name is available.
 */
function personalise(body, exampleName, recipient) {
  const first = (recipient.name || "").split(" ")[0] || "kunde";
  let out = body || "";
  if (exampleName) {
    // Case-sensitive whole-string replace of the example name.
    const safe = exampleName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(safe, "g"), first);
  }
  out = out.replace(/\{\{\s*navn\s*\}\}/gi, first);
  return out;
}

// ---------- Component ----------

export default function CampaignWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);            // 1 = audience, 2 = preview, 3 = saving
  const [audienceId, setAudienceId] = useState("inactive_60");
  const [typeId, setTypeId] = useState("missed_you");
  const [channel, setChannel] = useState("sms");

  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const [hint, setHint] = useState(CAMPAIGN_TYPES[0].defaultHint);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [exampleName, setExampleName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset state when modal closes so the next open starts fresh.
  useEffect(() => {
    if (open) return;
    setStep(1); setAudienceId("inactive_60"); setTypeId("missed_you");
    setChannel("sms"); setRecipients([]); setHint(CAMPAIGN_TYPES[0].defaultHint);
    setSubject(""); setBody(""); setExampleName("");
  }, [open]);

  // Whenever the campaign type changes, refresh the default hint (only if
  // the user hasn't typed something custom in yet).
  useEffect(() => {
    const t = typeFor(typeId);
    setHint((h) => (h && h !== "" ? h : t.defaultHint));
  }, [typeId]);

  // Fetch the recipient list for the chosen audience whenever audience /
  // channel change in step 1. We cap at 200 to keep the preview snappy.
  const audience = audienceFor(audienceId);
  useEffect(() => {
    if (!open || step !== 1) return;
    let alive = true;
    setRecipientsLoading(true);
    api.get("/customers", { params: audience.params })
      .then((res) => {
        if (!alive) return;
        let list = Array.isArray(res.data) ? res.data : [];
        if (audience.filter) list = list.filter(audience.filter);
        // Email campaigns can only target customers with an email address.
        if (channel === "email") list = list.filter((c) => !!c.email);
        setRecipients(list.slice(0, 200));
      })
      .catch(() => { if (alive) setRecipients([]); })
      .finally(() => { if (alive) setRecipientsLoading(false); });
    return () => { alive = false; };
  }, [open, step, audienceId, channel, audience]);

  const recipientCount = recipients.length;
  const canProceed = recipientCount > 0;

  // ---------- Step 1 → 2: generate preview ----------
  const goToPreview = async () => {
    const t = typeFor(typeId);
    const example = recipients[0];
    const exampleFirst = (example?.name || "Kari").split(" ")[0];
    setExampleName(exampleFirst);

    if (!t.purpose) {
      // Custom message: skip AI, start with empty body.
      setSubject(channel === "email" ? "Melding fra oss" : "");
      setBody("");
      setStep(2);
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post("/ai/generate-followup", {
        purpose: t.purpose,
        channel,
        tone: "vennlig",
        customer_name: exampleFirst,
        service_name: example?.last_service || "",
        custom_hint: hint || t.defaultHint,
      });
      setSubject(res.data?.subject || "");
      setBody(res.data?.body || "");
      setStep(2);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Kampanje med AI krever Pro eller Premium-plan.");
      } else {
        toast.error(err?.response?.data?.detail || "Kunne ikke generere melding");
      }
    } finally {
      setGenerating(false);
    }
  };

  // ---------- Step 2 → 3: save as drafts ----------
  const saveDrafts = async () => {
    if (!body?.trim()) { toast.error("Meldingsteksten kan ikke være tom"); return; }
    setSaving(true);
    let ok = 0;
    let fail = 0;
    for (const r of recipients) {
      try {
        await api.post("/followups", {
          customer_name: r.name,
          channel,
          subject: channel === "email" ? (subject || null) : null,
          body: personalise(body, exampleName, r),
          tone: "vennlig",
          purpose: typeFor(typeId).purpose || "egendefinert",
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSaving(false);
    if (ok > 0) {
      toast.success(
        fail > 0
          ? `${ok} utkast lagret · ${fail} feilet`
          : `${ok} utkast lagret i Meldinger`,
      );
    } else {
      toast.error("Kunne ikke lagre utkast");
    }
    if (onDone) onDone({ ok, fail });
    onOpenChange(false);
  };

  // ---------- Render ----------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl" data-testid="campaign-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
            {step === 1 ? "Opprett kampanje" : step === 2 ? "Forhåndsvis & rediger" : "Lagre utkast"}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-5">
            {/* Channel toggle */}
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Kanal</Label>
              <div className="mt-2 flex gap-2">
                {[
                  { id: "sms",   icon: MessageSquare, label: "SMS" },
                  { id: "email", icon: Mail,          label: "E-post" },
                ].map((c) => {
                  const Icon = c.icon;
                  const active = channel === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setChannel(c.id)}
                      className={`flex-1 rounded-xl border px-4 py-3 flex items-center gap-2 transition-all
                        ${active
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/30"
                          : "border-border bg-card hover:bg-accent/40"}`}
                      data-testid={`campaign-channel-${c.id}`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                      <span className="text-sm font-semibold">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Audience */}
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Målgruppe</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AUDIENCES.map((a) => {
                  const Icon = a.icon;
                  const active = audienceId === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAudienceId(a.id)}
                      className={`text-left rounded-xl border px-3 py-2.5 flex items-start gap-2.5 transition-all
                        ${active
                          ? "border-emerald-500/40 bg-emerald-500/10 ring-1 ring-emerald-500/30"
                          : "border-border bg-card hover:bg-accent/40"}`}
                      data-testid={`campaign-audience-${a.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ring-1 flex items-center justify-center shrink-0
                        ${active ? "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30" : "bg-muted text-muted-foreground ring-border"}`}>
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{a.label}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="campaign-recipient-count">
                {recipientsLoading
                  ? "Teller mottakere…"
                  : recipientCount === 0
                    ? "Ingen kunder matcher denne målgruppen for valgt kanal."
                    : `${recipientCount} ${recipientCount === 1 ? "mottaker" : "mottakere"} matcher.`}
              </p>
            </div>

            {/* Campaign type */}
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Kampanjetype</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CAMPAIGN_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = typeId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { setTypeId(t.id); setHint(t.defaultHint); }}
                      className={`text-left rounded-xl border px-3 py-2.5 flex items-start gap-2.5 transition-all
                        ${active
                          ? "border-emerald-500/40 bg-emerald-500/10 ring-1 ring-emerald-500/30"
                          : "border-border bg-card hover:bg-accent/40"}`}
                      data-testid={`campaign-type-${t.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ring-1 flex items-center justify-center shrink-0
                        ${active ? "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30" : "bg-muted text-muted-foreground ring-border"}`}>
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{t.label}</p>
                        <p className="text-xs text-muted-foreground truncate">{t.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hint to AI (only when AI is used) */}
            {typeFor(typeId).purpose && (
              <div>
                <Label htmlFor="campaign-hint" className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                  Hint til AI (valgfritt)
                </Label>
                <Textarea
                  id="campaign-hint"
                  rows={2}
                  className="rounded-xl mt-2"
                  placeholder="F.eks. 'Nevn at vi har 15 % rabatt frem til 1. juni'"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  data-testid="campaign-hint-input"
                />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {channel === "sms" ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
              <span className="uppercase tracking-widest font-semibold">{channel}</span>
              <Badge variant="outline" className="rounded-full text-[10px]">
                {recipientCount} {recipientCount === 1 ? "mottaker" : "mottakere"}
              </Badge>
              <Badge variant="outline" className="rounded-full text-[10px]">{typeFor(typeId).label}</Badge>
            </div>

            {channel === "email" && (
              <div>
                <Label htmlFor="campaign-subject">Emne</Label>
                <Input
                  id="campaign-subject"
                  className="rounded-xl mt-1.5"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="campaign-subject-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="campaign-body">
                Meldingstekst
                <span className="text-muted-foreground font-normal ml-2 text-xs">
                  · "{exampleName}" blir byttet til hver mottakers navn
                </span>
              </Label>
              <Textarea
                id="campaign-body"
                className="rounded-xl mt-1.5 font-mono text-sm"
                rows={9}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={`Skriv en kort, vennlig melding. Du kan bruke ${exampleName} eller {{navn}} som plassholder for kundens navn.`}
                data-testid="campaign-body-input"
              />
            </div>

            <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Eksempel — første mottaker
              </p>
              <p className="text-sm">
                <span className="font-semibold">Til:</span>{" "}
                {recipients[0]?.name || "—"}
                {channel === "email" && recipients[0]?.email ? ` <${recipients[0].email}>` : ""}
              </p>
              {channel === "email" && subject && (
                <p className="text-sm mt-1"><span className="font-semibold">Emne:</span> {subject}</p>
              )}
              <p className="text-sm mt-2 whitespace-pre-wrap" data-testid="campaign-preview-body">
                {personalise(body, exampleName, recipients[0] || { name: exampleName })}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="outline"
              className={SECONDARY_BTN_CLS}
              onClick={() => setStep(1)}
              disabled={saving}
              data-testid="campaign-back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Tilbake
            </Button>
          )}
          {step === 1 && (
            <Button
              className={PRIMARY_BTN_CLS}
              onClick={goToPreview}
              disabled={!canProceed || generating || recipientsLoading}
              data-testid="campaign-next-btn"
            >
              {generating
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Genererer…</>)
                : (<>Forhåndsvis <ChevronRight className="w-4 h-4" /></>)}
            </Button>
          )}
          {step === 2 && (
            <Button
              className={PRIMARY_BTN_CLS}
              onClick={saveDrafts}
              disabled={saving || !body?.trim()}
              data-testid="campaign-save-btn"
            >
              {saving
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Lagrer…</>)
                : (<><Check className="w-4 h-4" /> Lagre {recipientCount} utkast</>)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export the helper so unit tests can import it.
export { personalise };
